<script setup lang="ts">
const props = defineProps<{
  loading?: boolean
  disabled?: boolean
  secondary?: boolean
  small?: boolean
}>()

const emit = defineEmits<{
  (e: 'click'): void
}>()

function handleClick() {
  if (!props.disabled && !props.loading) {
    emit('click')
  }
}
</script>

<template>
  <button
    class="gildi-button linkbox-style"
    :class="{
      'is-loading': loading,
      'is-secondary': secondary,
      'is-small': small,
      'is-disabled': disabled || loading
    }"
    :disabled="disabled || loading"
    @click="handleClick"
  >
    <transition name="fade">
      <v-progress-circular
        v-if="loading"
        class="inline-spinner"
        color="white"
        indeterminate
        size="18"
      />
    </transition>
    <slot />
  </button>
</template>

<style scoped lang="scss">
@use "@/scss/settings/variables" as var;

/* LinkBox-like look */
.linkbox-style {
  /* Base from link-action-box__button */
  background-color: var.$color-secondary;
  color: #fff;
  padding: 10px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  line-height: 1; /* keeps height tidy like link button */
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;
  border: none;
  cursor: pointer;

  &:hover {
    background-color: var.$color-secondary-muted;
  }

  &.is-disabled {
    background-color: #ccc;
    color: #666;
    cursor: not-allowed;
  }

  /* Loading state matches disabled visuals but keeps white spinner visible */
  &.is-loading {
    cursor: progress;
  }

  .inline-spinner {
    margin-right: 6px;
  }

  /* Secondary variant (outlined to harmonize with your card look) */
  &.is-secondary {
    background-color: transparent;
    color: var.$color-secondary;
    border: 1px solid var.$color-border;

    &:hover {
      background-color: var.$color-background; /* subtle hover */
      color: var.$color-secondary;
      border-color: var.$color-border;
    }

    &.is-disabled {
      background-color: transparent;
      color: #8aa0a6; /* muted */
      border-color: var.$color-border;
    }

    /* Spinner stays visible; uses currentColor on outlined */
    .inline-spinner {
      /* Vuetify spinner uses its own color prop; default above is white.
         For secondary we want it to match text color: */
      color: currentColor !important;
    }
  }

  /* Small size option */
  &.is-small {
    padding: 8px 12px;
    font-size: 13px;
    border-radius: 8px;
  }
}

/* Legacy transition retained for spinner mount/unmount */
.fade-enter-active,
.fade-leave-active {
  transition: width 0.2s, opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  width: 0;
  opacity: 0;
}
</style>
